import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import { Offcanvas, Button, Form, Row, Col, Dropdown } from 'react-bootstrap';
import { BsX } from "react-icons/bs";
import { 
  FaBuilding, 
  FaFileContract, 
  FaCheckCircle, 
  FaHistory, 
  FaExclamationCircle, 
  FaPlus 
} from "react-icons/fa";
import LicenseInfoDetails from '../v360-license-info/LicenseInfoDetails';
import axios from 'axios';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactPaginate from 'react-paginate';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  const token = userInfo?.token;
  const origin = localStorage.getItem('user-origin');
  return {
    Authorization: `Bearer ${token}`,
    'Tenant-Origin': origin
  };
};

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="btn rounded border d-flex align-items-center justify-content-center shadow-sm bg-white text-muted px-3"
    style={{ height: '38px' }}
    title="Filter by Expiry"
  >
    <i className="mdi mdi-tune font-size-18 me-2"></i> Filter
  </button>
));

const parseValidDate = (dateStr) => {
  if (!dateStr) return null;
  const datePart = String(dateStr).split('T')[0].trim();
  const parts = datePart.split('-');
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      const year = Number(parts[0]);
      const month = Number(parts[1]) - 1;
      const day = Number(parts[2]);
      const d = new Date(year, month, day);
      return isNaN(d.getTime()) ? null : d;
    } else if (parts[2].length === 4) {
      const day = Number(parts[0]);
      const month = Number(parts[1]) - 1;
      const year = Number(parts[2]);
      const d = new Date(year, month, day);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = date instanceof Date ? date : parseValidDate(date);
  if (!d || isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateToIso = (dateStr) => {
  if (!dateStr) return null;
  const str = String(dateStr).trim();
  const parts = str.split('-');
  if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return str;
};

const getRowColorClass = (info) => {
  const validityDateStr = info.extended_validity || info.validity;
  if (!validityDateStr) return '';
  const validityDate = parseValidDate(validityDateStr);
  if (!validityDate) return '';
  validityDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = validityDate - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'text-danger fw-bold';
  } else if (diffDays <= 30) {
    return 'text-warning fw-bold';
  }
  return '';
};

const getLicenseStatus = (info) => {
  const validityDateStr = info.extended_validity || info.validity;
  if (!validityDateStr) return 'Active';
  const validityDate = parseValidDate(validityDateStr);
  if (!validityDate) return 'Active';
  validityDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = validityDate - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays >= -7 && diffDays < 0) return 'Expired in 7 days';
  if (diffDays < 0) return 'Expired';
  if (info.extended_validity) return 'Extended';
  return 'Active';
};

const getDaysRemaining = (validityStr) => {
  if (!validityStr) return '-';
  const validityDate = parseValidDate(validityStr);
  if (!validityDate) return '-';
  validityDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = validityDate - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires Today';
  return `${diffDays} Days`;
};

const TenantInfoDetails = ({ show, onHide, tenantData, setupId, onLicensesUpdated, autoOpenLicense }) => {
  const [localLicenses, setLocalLicenses] = useState(tenantData?.licenses || []);

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 15;

  // Modals inside TenantInfoDetails
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLicenseId, setDetailsLicenseId] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendData, setExtendData] = useState({ type: 'days', value: 1 });
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalValidityDate, setModalValidityDate] = useState(null);
  const [userConcurrency, setUserConcurrency] = useState('1:1');
  const [licenseStatus, setLicenseStatus] = useState('active');
  const [rolesState, setRolesState] = useState({
    role_s_admin: 0,
    role_admin: 0,
    role_supervisor: 0,
    role_teamlead: 0,
    role_analyst: 0,
    role_cagent: 0,
    role_vagent: 0,
    role_tagent: 0,
    role_agent: 0,
    role_agent_plus: 0,
  });

  const calculatedUserLogin = useMemo(() => {
    return Object.values(rolesState).reduce((acc, val) => acc + (Number(val) || 0), 0);
  }, [rolesState]);

  const calculatedUserCreate = useMemo(() => {
    let multiplier = 1;
    if (userConcurrency === '2:1') multiplier = 2;
    else if (userConcurrency === '3:1') multiplier = 3;
    return calculatedUserLogin * multiplier;
  }, [calculatedUserLogin, userConcurrency]);

  const handleRoleChange = (roleName, value) => {
    const num = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setRolesState(prev => ({
      ...prev,
      [roleName]: num
    }));
  };

  useEffect(() => {
    if (tenantData?.licenses) {
      setLocalLicenses(tenantData.licenses);
      setCurrentPage(0);
    }
  }, [tenantData]);

  // Auto-open the Add License form when triggered from empty state
  useEffect(() => {
    if (show && autoOpenLicense) {
      // Reset modal state and open add form
      setCurrentRecord(null);
      setModalStartDate(null);
      setModalValidityDate(null);
      setUserConcurrency('1:1');
      setLicenseStatus('active');
      setRolesState({
        role_s_admin: 0, role_admin: 0, role_supervisor: 0,
        role_teamlead: 0, role_analyst: 0, role_cagent: 0,
        role_vagent: 0, role_tagent: 0, role_agent: 0, role_agent_plus: 0,
      });
      setShowLicenseModal(true);
    }
    if (!show) {
      // Reset when panel closes
      setShowLicenseModal(false);
      setLocalLicenses([]);
      setSearchQuery('');
      setExpiryFilter('');
      setCurrentPage(0);
    }
  }, [show, autoOpenLicense]);

  // Compute Active, Extended, Expired, and Total license counts
  const licenseCounts = useMemo(() => {
    let active = 0;
    let extended = 0;
    let expired = 0;

    localLicenses.forEach(lic => {
      const status = getLicenseStatus(lic);
      if (status === 'Expired') {
        expired += 1;
      } else if (status === 'Extended') {
        extended += 1;
      } else {
        active += 1;
      }
    });

    return {
      total: localLicenses.length,
      active,
      extended,
      expired
    };
  }, [localLicenses]);

  // Filtered licenses for the table
  const filteredLicenses = useMemo(() => {
    let list = localLicenses || [];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(lic => {
        const remarkMatch = lic.remark && lic.remark.toLowerCase().includes(q);
        const idMatch = String(lic.id).includes(q);
        const tenantMatch = String(lic.tenant_id ?? '').toLowerCase().includes(q);
        return remarkMatch || idMatch || tenantMatch;
      });
    }

    if (expiryFilter) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      list = list.filter(lic => {
        const valStr = lic.extended_validity || lic.validity;
        if (!valStr) return false;
        const d = parseValidDate(valStr);
        if (!d) return false;
        d.setHours(0, 0, 0, 0);
        const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));

        if (expiryFilter === 'expired') {
          return diffDays < 0;
        } else if (expiryFilter === 'expired_7_days') {
          return diffDays >= -7 && diffDays < 0;
        } else if (expiryFilter === '1_day') {
          return diffDays === 1;
        } else if (expiryFilter === '7_days') {
          return diffDays > 0 && diffDays <= 7;
        } else if (expiryFilter === '30_days') {
          return diffDays > 0 && diffDays <= 30;
        }
        return true;
      });
    }

    return list;
  }, [localLicenses, searchQuery, expiryFilter]);

  const pageCount = Math.ceil(filteredLicenses.length / ITEMS_PER_PAGE);
  const paginatedLicenses = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredLicenses.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredLicenses, currentPage]);

  const refreshLicenses = async () => {
    if (setupId) {
      try {
        const res = await axios.get(`${backendUrl}/api/v360-license-info`, {
          params: { setup_id: setupId },
          headers: getHeaders()
        });
        const allLics = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const tenantLics = allLics.filter(l => String(l.tenant_id ?? '') === String(tenantData?.tenant_id ?? ''));
        setLocalLicenses(tenantLics);
      } catch (err) {
        console.error('Error refetching tenant licenses:', err);
      }
    }
    if (onLicensesUpdated) {
      onLicensesUpdated();
    }
  };

  const handleViewLicenseDetails = (record) => {
    setCurrentRecord(record);
    setDetailsLicenseId(record.id);
    setShowDetailsModal(true);
  };

  const handleOpenExtendModal = (record) => {
    setCurrentRecord(record);
    setExtendData({ type: 'days', value: 1 });
    setShowExtendModal(true);
  };

  const handleShowLicenseModal = (record = null) => {
    setCurrentRecord(record);
    if (record) {
      setModalStartDate(record.start_date ? new Date(record.start_date) : null);
      const activeValidity = record.extended_validity || record.validity;
      setModalValidityDate(activeValidity ? new Date(activeValidity) : null);
      setUserConcurrency(record.user_concurrency || '1:1');
      setLicenseStatus(record.license_status || 'active');
      setRolesState({
        role_s_admin: Number(record.role_s_admin) || 0,
        role_admin: Number(record.role_admin) || 0,
        role_supervisor: Number(record.role_supervisor) || 0,
        role_teamlead: Number(record.role_teamlead) || 0,
        role_analyst: Number(record.role_analyst) || 0,
        role_cagent: Number(record.role_cagent) || 0,
        role_vagent: Number(record.role_vagent) || 0,
        role_tagent: Number(record.role_tagent) || 0,
        role_agent: Number(record.role_agent) || 0,
        role_agent_plus: Number(record.role_agent_plus) || 0,
      });
    } else {
      setModalStartDate(null);
      setModalValidityDate(null);
      setUserConcurrency('1:1');
      setLicenseStatus('active');
      setRolesState({
        role_s_admin: 0,
        role_admin: 0,
        role_supervisor: 0,
        role_teamlead: 0,
        role_analyst: 0,
        role_cagent: 0,
        role_vagent: 0,
        role_tagent: 0,
        role_agent: 0,
        role_agent_plus: 0,
      });
    }
    setShowLicenseModal(true);
  };

  const handleCloseLicenseModal = () => {
    setCurrentRecord(null);
    setModalStartDate(null);
    setModalValidityDate(null);
    setUserConcurrency('1:1');
    setLicenseStatus('active');
    setRolesState({
      role_s_admin: 0,
      role_admin: 0,
      role_supervisor: 0,
      role_teamlead: 0,
      role_analyst: 0,
      role_cagent: 0,
      role_vagent: 0,
      role_tagent: 0,
      role_agent: 0,
      role_agent_plus: 0,
    });
    setShowLicenseModal(false);
    // If opened purely to add a license, close the parent panel too
    if (autoOpenLicense) {
      onHide();
    }
  };

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, {
        extension_type: extendData.type,
        extension_days: parseInt(extendData.value, 10)
      }, { headers: getHeaders() });
      Toast.fire({ icon: 'success', title: 'License extended successfully' });
      setShowExtendModal(false);
      refreshLicenses();
    } catch (error) {
      console.error('Error extending license:', error);
      Toast.fire({ icon: 'error', title: 'Failed to extend license' });
    }
  };

  const handleDeleteLicense = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${backendUrl}/api/v360-license-info/${id}`, { headers: getHeaders() });
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
          refreshLicenses();
        } catch (error) {
          console.error('Error deleting record', error);
          Swal.fire('Error!', 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const handleSubmitLicense = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    const numericFields = [
      'setup_id', 'tenant_id', 'user_login', 'user_create', 
      'role_s_admin', 'role_admin', 'role_supervisor', 'role_teamlead', 'role_analyst', 
      'role_cagent', 'role_vagent', 'role_tagent', 'role_agent', 'mobile_app', 
      'module_email', 'module_voicemail', 'module_chat', 'module_interaction', 
      'module_voice', 'module_api', 'role_agent_plus', 'module_video', 'wallboard', 
      'gen_ai', 'toolbar_app', 'toolbar_app_sup', 'ai_assist', 'ai_assist_pro',
      'module_ticket', 'camp_voice', 'camp_waba', 'camp_sms', 'camp_email'
    ];
    
    for (let field of numericFields) {
      if (data[field] !== undefined) {
        if (data[field] === "") {
          data[field] = 0;
        } else {
          const parsed = Number(data[field]);
          if (isNaN(parsed)) {
            Toast.fire({ icon: 'error', title: `${field.replace('_', ' ').toUpperCase()} must be a valid number` });
            return;
          }
          data[field] = parsed;
        }
      }
    }

    data.user_login = calculatedUserLogin;
    data.user_create = calculatedUserCreate;
    data.user_concurrency = userConcurrency;
    data.license_status = licenseStatus;
    data.toolbar_app_sup = Number(data.toolbar_app_sup) || 0;

    data.start_date = formatDateToIso(data.start_date);
    data.validity = formatDateToIso(data.validity);
    data.ip_check = formData.get('ip_check') === 'on';
    data.ips = data.ips ? String(data.ips).split(',').map(ip => ip.trim()).filter(Boolean) : [];

    // Set tenant_name & tenant_id: preserve from form, tenantData, or currentRecord
    if (!data.tenant_id && tenantData?.tenant_id !== undefined && tenantData?.tenant_id !== null) {
      data.tenant_id = tenantData.tenant_id;
    }
    if (!data.tenant_name && tenantData?.tenant_name && tenantData.tenant_name !== '-') {
      data.tenant_name = tenantData.tenant_name;
    } else if (!data.tenant_name && currentRecord?.tenant_name) {
      data.tenant_name = currentRecord.tenant_name;
    }

    // Ensure tenant-level acc_status and status_reason are inherited / preserved
    if (!currentRecord?.id) {
      data.acc_status = tenantData?.acc_status || 'active';
      if (tenantData?.status_reason) data.status_reason = tenantData.status_reason;
    } else {
      if (tenantData?.acc_status) data.acc_status = tenantData.acc_status;
      if (tenantData?.status_reason !== undefined) data.status_reason = tenantData.status_reason;
    }

    try {
      if (currentRecord && currentRecord.id) {
        await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/v360-license-info`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record created successfully' });
      }
      handleCloseLicenseModal();
      refreshLicenses();
    } catch (error) {
      console.error('Error saving record', error);
      Toast.fire({ icon: 'error', title: 'Failed to save record' });
    }
  };

  const isSubModalOpen = showDetailsModal || showExtendModal || showLicenseModal;
  const isTenantFixed = Boolean(
    (tenantData?.tenant_id !== undefined && tenantData?.tenant_id !== null) || currentRecord?.id
  );

  const displayTenantName = useMemo(() => {
    if (tenantData?.tenant_name && tenantData.tenant_name !== '-' && String(tenantData.tenant_name).trim() !== '') {
      return String(tenantData.tenant_name).trim();
    }
    const fromLic = localLicenses.find(l => l.tenant_name && String(l.tenant_name).trim() && String(l.tenant_name).trim() !== '-');
    if (fromLic) {
      return String(fromLic.tenant_name).trim();
    }
    return tenantData?.tenant_id !== null && tenantData?.tenant_id !== undefined ? `Tenant ${tenantData.tenant_id}` : 'Tenant Details';
  }, [tenantData, localLicenses]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={onHide}
        placement="end"
        style={{ 
          width: '1180px', 
          zIndex: 1050,
          visibility: isSubModalOpen ? 'hidden' : 'visible'
        }}
        className="Setup-offcanvas"
      >
        {/* Offcanvas Header - identical style to SetupInfoDetails */}
        <Offcanvas.Header className="border-bottom bg-white py-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <div className="bg-primary rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                <FaBuilding className="text-white" size={18} />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0 fw-bold text-dark">{displayTenantName}</h5>
                  <span className={`badge ${
                    tenantData?.acc_status === 'deactivated' 
                      ? 'bg-danger' 
                      : tenantData?.acc_status === 'suspended' 
                      ? 'bg-warning text-dark' 
                      : 'bg-success'
                  }`}>
                    {tenantData?.acc_status || 'active'}
                  </span>
                </div>
                <small className="text-muted">Tenant ID: {tenantData?.tenant_id !== null ? tenantData?.tenant_id : 'Unassigned'}</small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="light"
                size="sm"
                onClick={onHide}
                className="border-0"
              >
                <BsX size={20} />
              </Button>
            </div>
          </div>
        </Offcanvas.Header>

        {/* Offcanvas Body */}
        <Offcanvas.Body className="p-0 bg-light">
          {/* Top Summary Card - exact same style as SetupInfoDetails */}
          <div className="p-4 border-bottom bg-white">
            <div className="row g-4">
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                    <FaFileContract className="text-primary" size={16} />
                  </div>
                  <div>
                    <label className="text-muted small mb-1 d-block">Total Licenses</label>
                    <h6 className="mb-0 fw-semibold text-dark">
                      {licenseCounts.total}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                    <FaCheckCircle className="text-success" size={16} />
                  </div>
                  <div>
                    <label className="text-muted small mb-1 d-block">Active Licenses</label>
                    <h6 className="mb-0 fw-semibold text-dark">
                      {licenseCounts.active}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                    <FaHistory className="text-info" size={16} />
                  </div>
                  <div>
                    <label className="text-muted small mb-1 d-block">Extended Licenses</label>
                    <h6 className="mb-0 fw-semibold text-dark">
                      {licenseCounts.extended}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                    <FaExclamationCircle className="text-danger" size={16} />
                  </div>
                  <div>
                    <label className="text-muted small mb-1 d-block">Expired Licenses</label>
                    <h6 className="mb-0 fw-semibold text-dark">
                      {licenseCounts.expired}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Table Container */}
          <div className="bg-white">
            <div className="p-3">
              {/* Top Controls: Search, Expiry Filter, and Add License */}
              <div className="d-flex justify-content-between mb-3 mt-2">
                <div className="d-flex">
                  <div className="search-box me-2 mb-2 d-inline-block">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search license or remark..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(0);
                        }}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Dropdown>
                    <Dropdown.Toggle as={CustomToggle} />
                    <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                      <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
                      <Dropdown.Item active={expiryFilter === ''} onClick={() => { setExpiryFilter(''); setCurrentPage(0); }}>All Licenses</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => { setExpiryFilter('expired'); setCurrentPage(0); }}>
                        <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
                      </Dropdown.Item>
                      <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => { setExpiryFilter('expired_7_days'); setCurrentPage(0); }}>
                        <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => { setExpiryFilter('1_day'); setCurrentPage(0); }}>
                        <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
                      </Dropdown.Item>
                      <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => { setExpiryFilter('7_days'); setCurrentPage(0); }}>
                        <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
                      </Dropdown.Item>
                      <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => { setExpiryFilter('30_days'); setCurrentPage(0); }}>
                        <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleShowLicenseModal({
                      tenant_id: tenantData?.tenant_id,
                      setup_id: setupId
                    })}
                    className="d-flex align-items-center"
                    style={{ height: '38px' }}
                  >
                    <FaPlus className="me-1" /> Add License
                  </Button>
                </div>
              </div>

              {/* Modern Table matching SetupInfoDetails */}
              <div className="contacts-table-modern">
                <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px', paddingBottom: '100px' }}>
                  <table className="table table-modern align-middle">
                    <thead>
                      <tr>
                        <th className="col" style={{ width: '60px' }}>Sl No</th>
                        <th className="col">
                          <div className="d-flex justify-content-center">
                            <div style={{ width: '150px', textAlign: 'left' }}>
                              Remark
                            </div>
                          </div>
                        </th>
                        <th className="col">Start Date</th>
                        <th className="col">Validity</th>
                        <th className="col">Days Remaining</th>
                        <th className="col">License Status</th>
                        <th className="col" style={{ width: '80px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLicenses.length > 0 ? (
                        paginatedLicenses.map((lic, licIdx) => {
                          const licRowColor = getRowColorClass(lic);
                          const licValidity = lic.extended_validity || lic.validity;
                          const licStatus = getLicenseStatus(lic);

                          let badgeClass = 'bg-success';
                          if (licStatus === 'Expired') {
                            badgeClass = 'bg-danger';
                          } else if (licStatus === 'Expired in 7 days') {
                            badgeClass = 'bg-soft-danger text-danger border border-danger';
                          } else if (licStatus === 'Extended') {
                            badgeClass = 'bg-soft-success text-success border border-success';
                          }

                          return (
                            <tr key={lic.id || licIdx}>
                              <td className={licRowColor}>
                                {(currentPage * ITEMS_PER_PAGE) + licIdx + 1}
                              </td>
                              <td 
                                className={`${licRowColor} text-primary fw-medium`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleViewLicenseDetails(lic)}
                                title="Click to view license details"
                              >
                                <div className="d-flex justify-content-center">
                                  <div style={{ width: '150px', textAlign: 'left' }}>
                                    <span className="text-break">{lic.remark || `-`}</span>
                                  </div>
                                </div>
                              </td>
                              
                              <td className={licRowColor}
                               style={{ cursor: 'pointer' }}
                                onClick={() => handleViewLicenseDetails(lic)}
                                title="Click to view license details"
                              >
                                {lic.start_date ? formatDate(lic.start_date) : '-'}
                              </td>
                              <td className={licRowColor}>
                                {licValidity ? formatDate(licValidity) : '-'}
                              </td>
                              <td className={licRowColor}>
                                {getDaysRemaining(licValidity)}
                              </td>
                              <td>
                                <span className={`badge ${badgeClass}`}>
                                  {licStatus.toLowerCase()}
                                </span>
                              </td>
                              <td>
                                <div className="dropdown">
                                  <i
                                    className="mdi mdi-dots-horizontal font-size-18"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    style={{ cursor: 'pointer' }}
                                  ></i>
                                  <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                    <li>
                                      <button 
                                        className="dropdown-item text-primary" 
                                        onClick={() => handleViewLicenseDetails(lic)}
                                      >
                                        View Details
                                      </button>
                                    </li>
                                    <li>
                                      <button 
                                        className="dropdown-item text-success" 
                                        onClick={() => handleOpenExtendModal(lic)}
                                      >
                                        Extend Date
                                      </button>
                                    </li>
                                    <li>
                                      <button 
                                        className="dropdown-item" 
                                        onClick={() => handleShowLicenseModal(lic)}
                                      >
                                        Edit
                                      </button>
                                    </li>
                                    <li>
                                      <button 
                                        className="dropdown-item text-danger" 
                                        onClick={() => handleDeleteLicense(lic.id)}
                                      >
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No License Info available for this tenant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pageCount > 1 && (
                  <div className="d-flex justify-content-end mt-3">
                    <ReactPaginate
                      previousLabel={"<"}
                      nextLabel={">"}
                      breakLabel={"..."}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={3}
                      onPageChange={(data) => setCurrentPage(data.selected)}
                      forcePage={currentPage}
                      containerClassName={"pagination pagination-rounded"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextClassName={"page-item"}
                      nextLinkClassName={"page-link"}
                      breakClassName={"page-item"}
                      breakLinkClassName={"page-link"}
                      activeClassName={"active"}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Individual License Details Modal (LicenseInfoDetails component) */}
      {showDetailsModal && detailsLicenseId && (
        <LicenseInfoDetails
          show={showDetailsModal}
          onHide={() => {
            setShowDetailsModal(false);
            setDetailsLicenseId(null);
          }}
          licenseId={detailsLicenseId}
          onEditLicense={(lic) => {
            setShowDetailsModal(false);
            handleShowLicenseModal(lic);
          }}
        />
      )}

      {/* Extend License Date Offcanvas Modal */}
      <Offcanvas 
        show={showExtendModal} 
        onHide={() => setShowExtendModal(false)} 
        placement="end" 
        style={{ width: '500px', zIndex: 1065 }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Extend License Date</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleExtendSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Value</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="1"
                    required
                    value={extendData.value}
                    onChange={e => setExtendData({ ...extendData, value: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Type</Form.Label>
                  <Form.Select 
                    value={extendData.type}
                    onChange={e => setExtendData({ ...extendData, type: e.target.value })}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={() => setShowExtendModal(false)} className="me-2">Cancel</Button>
              <Button variant="success" type="submit">Extend License</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Add / Edit License Offcanvas Modal */}
      <Offcanvas 
        show={showLicenseModal} 
        onHide={handleCloseLicenseModal} 
        placement="end" 
        style={{ width: '1180px', zIndex: 1060 }} 
        className="custom-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-bold text-primary">
            {currentRecord?.id ? 'Edit License Info' : 'Add License Info'}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <hr style={{ margin: 0 }} />
        <Offcanvas.Body>
          <Form onSubmit={handleSubmitLicense}>
            <h5 className="mb-3 text-secondary">Basic Info</h5>
            <Row>
              <input 
                type="hidden" 
                name="setup_id" 
                value={currentRecord?.setup_id || setupId || ''} 
              />
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Tenant ID</Form.Label>
                  <Form.Control 
                    className={`form-feild ${isTenantFixed ? 'bg-light text-muted' : ''}`}
                    type="number" 
                    name="tenant_id" 
                    defaultValue={currentRecord?.tenant_id ?? (tenantData?.tenant_id !== undefined && tenantData?.tenant_id !== null ? tenantData.tenant_id : '')} 
                    readOnly={isTenantFixed}
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Tenant Name</Form.Label>
                  <Form.Control 
                    className={`form-feild ${isTenantFixed ? 'bg-light text-muted' : ''}`}
                    type="text" 
                    name="tenant_name" 
                    defaultValue={currentRecord?.tenant_name || (displayTenantName !== 'Tenant Details' ? displayTenantName : '')} 
                    readOnly={isTenantFixed}
                    placeholder="Enter Tenant Name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-3 text-secondary mt-2">Dates</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 w-100">
                  <Form.Label className="form-label form-font">Start Date</Form.Label>
                  <DatePicker
                    selected={modalStartDate}
                    onChange={(date) => setModalStartDate(date)}
                    className="form-control form-feild w-100"
                    wrapperClassName="w-100"
                    dateFormat="dd-MM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="Select Start Date"
                    name="start_date"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 w-100">
                  <Form.Label className="form-label form-font">Validity</Form.Label>
                  <DatePicker
                    selected={modalValidityDate}
                    onChange={(date) => setModalValidityDate(date)}
                    className="form-control form-feild w-100"
                    wrapperClassName="w-100"
                    dateFormat="dd-MM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="Select Validity Date"
                    name="validity"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-3 text-secondary mt-2">Licenses</h5>
            <Row>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Super Admin</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_s_admin" 
                    value={rolesState.role_s_admin || ''} 
                    onChange={(e) => handleRoleChange('role_s_admin', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Admin</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_admin" 
                    value={rolesState.role_admin || ''} 
                    onChange={(e) => handleRoleChange('role_admin', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Supervisor</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_supervisor" 
                    value={rolesState.role_supervisor || ''} 
                    onChange={(e) => handleRoleChange('role_supervisor', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Teamlead</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_teamlead" 
                    value={rolesState.role_teamlead || ''} 
                    onChange={(e) => handleRoleChange('role_teamlead', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Analyst</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_analyst" 
                    value={rolesState.role_analyst || ''} 
                    onChange={(e) => handleRoleChange('role_analyst', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">C. Agent</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_cagent" 
                    value={rolesState.role_cagent || ''} 
                    onChange={(e) => handleRoleChange('role_cagent', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">V. Agent</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_vagent" 
                    value={rolesState.role_vagent || ''} 
                    onChange={(e) => handleRoleChange('role_vagent', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Ticket Agent</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_tagent" 
                    value={rolesState.role_tagent || ''} 
                    onChange={(e) => handleRoleChange('role_tagent', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Omni Agent</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_agent" 
                    value={rolesState.role_agent || ''} 
                    onChange={(e) => handleRoleChange('role_agent', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Agent Plus</Form.Label>
                  <Form.Control 
                    className="form-feild" 
                    type="number" 
                    name="role_agent_plus" 
                    value={rolesState.role_agent_plus || ''} 
                    onChange={(e) => handleRoleChange('role_agent_plus', e.target.value)} 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">User Concurrency</Form.Label>
                  <Form.Select 
                    className="form-feild" 
                    name="user_concurrency" 
                    value={userConcurrency}
                    onChange={(e) => setUserConcurrency(e.target.value)}
                  >
                    <option value="1:1">1:1</option>
                    <option value="2:1">2:1</option>
                    <option value="3:1">3:1</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">User Login (Total Roles)</Form.Label>
                  <Form.Control 
                    className="form-feild bg-light text-muted fw-bold" 
                    type="number" 
                    name="user_login" 
                    value={calculatedUserLogin} 
                    readOnly 
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">User Create (Calculated)</Form.Label>
                  <Form.Control 
                    className="form-feild bg-light text-muted fw-bold" 
                    type="number" 
                    name="user_create" 
                    value={calculatedUserCreate} 
                    readOnly 
                  />
                </Form.Group>
              </Col> */}
            </Row>

            <h5 className="mb-3 text-secondary mt-2">Modules & Features</h5>
            <Row>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Email</Form.Label><Form.Control className="form-feild" type="number" name="module_email" defaultValue={currentRecord?.module_email ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voicemail</Form.Label><Form.Control className="form-feild" type="number" name="module_voicemail" defaultValue={currentRecord?.module_voicemail ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Chat</Form.Label><Form.Control className="form-feild" type="number" name="module_chat" defaultValue={currentRecord?.module_chat ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Interaction</Form.Label><Form.Control className="form-feild" type="number" name="module_interaction" defaultValue={currentRecord?.module_interaction ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voice</Form.Label><Form.Control className="form-feild" type="number" name="module_voice" defaultValue={currentRecord?.module_voice ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Video</Form.Label><Form.Control className="form-feild" type="number" name="module_video" defaultValue={currentRecord?.module_video ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">API</Form.Label><Form.Control className="form-feild" type="number" name="module_api" defaultValue={currentRecord?.module_api ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Ticket</Form.Label><Form.Control className="form-feild" type="number" name="module_ticket" defaultValue={currentRecord?.module_ticket ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Voice</Form.Label><Form.Control className="form-feild" type="number" name="camp_voice" defaultValue={currentRecord?.camp_voice ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Waba</Form.Label><Form.Control className="form-feild" type="number" name="camp_waba" defaultValue={currentRecord?.camp_waba ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp SMS</Form.Label><Form.Control className="form-feild" type="number" name="camp_sms" defaultValue={currentRecord?.camp_sms ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Email</Form.Label><Form.Control className="form-feild" type="number" name="camp_email" defaultValue={currentRecord?.camp_email ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Mobile App</Form.Label><Form.Control className="form-feild" type="number" name="mobile_app" defaultValue={currentRecord?.mobile_app ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Wallboard</Form.Label><Form.Control className="form-feild" type="number" name="wallboard" defaultValue={currentRecord?.wallboard ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Gen AI</Form.Label><Form.Control className="form-feild" type="number" name="gen_ai" defaultValue={currentRecord?.gen_ai ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Toolbar App</Form.Label><Form.Control className="form-feild" type="number" name="toolbar_app" defaultValue={currentRecord?.toolbar_app ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Toolbar App Sup</Form.Label><Form.Control className="form-feild" type="number" name="toolbar_app_sup" defaultValue={currentRecord?.toolbar_app_sup ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Assist</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist" defaultValue={currentRecord?.ai_assist ?? ''} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Pro</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist_pro" defaultValue={currentRecord?.ai_assist_pro ?? ''} /></Form.Group></Col>
            </Row>

            <h5 className="mb-3 text-secondary mt-2">IP & Remarks</h5>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3 mt-4">
                  <Form.Check type="checkbox" label={<span className="form-label form-font">Enable IP Check</span>} name="ip_check" defaultChecked={currentRecord?.ip_check || false} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">IP List</Form.Label>
                  <Form.Control className="form-feild" type="text" name="ips" defaultValue={currentRecord?.ips ? currentRecord.ips.join(', ') : ''} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Remark</Form.Label>
                  <Form.Control className="form-feild" as="textarea" rows={2} name="remark" defaultValue={currentRecord?.remark || ''} placeholder="Enter Remark" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">Remarks (Additional)</Form.Label>
                  <Form.Control className="form-feild" as="textarea" rows={2} name="remarks" defaultValue={currentRecord?.remarks || ''} />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4 mb-4">
              <Button variant="secondary" onClick={handleCloseLicenseModal} className="me-2">Cancel</Button>
              <Button variant="primary" type="submit">Save</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default TenantInfoDetails;
